import { useContext, useState, useMemo } from 'react';
import { Box, Typography, Popover } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AuctionContext } from '../../context/auctionContext';
import { UserContext } from '../../context/userContext';
import { LOT_STATUS } from '../../utils/constants';
import { NormalText } from '../StyledComponent';
import formatMoney from '../../utils/formatMoney';


const useStyles = makeStyles((theme) => ({
  navBarTextBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    cursor: 'pointer',
    borderRadius: theme.spacing(1),
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
      transform: 'translateY(-1px)',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  title: {
    fontSize: 20,
    textAlign: 'center',
    color: '#ffff',
    fontWeight: 600,
    [theme.breakpoints.down('md')]: {
      color: theme.palette.primary.main,
    },
  },
  titleSmall: {
    fontSize: 14,
    opacity: 0.95,
    color: '#e8f2ff',
    fontWeight: 500,
    textAlign: 'center',
  },
  total: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
    color: '#ffffff',
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
    textAlign: 'center',
  },
  paraph: {
    fontSize: 12,
    textAlign: 'center',
    color: '#e8f2ff',
    fontFamily: 'ITC Officina Sans Std, Book',
    lineHeight: 1.4,
    backgroundColor: 'rgba(255,255,255,0.1)',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    border: '1px solid rgba(255,255,255,0.2)',
    marginBottom: theme.spacing(1),
  },
  awardItem: {
    flex: 1,
    minHeight: 60,
    display: 'flex',
    backgroundColor: '#ffffff',
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    border: '1px solid #b8d4f0',
    boxShadow: '0 2px 8px rgba(0, 59, 112, 0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0, 59, 112, 0.25)',
      transform: 'translateY(-1px)',
    },
  },
  awardImgBox: {
    flex: 1,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    backgroundColor: '#f5f8fc',
  },
  awardInfoBox: {
    flex: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
  popoverBox: {
    maxWidth: 320,
    backgroundColor: theme.palette.primary.main,
    border: '1px solid #002950',
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    borderRadius: theme.spacing(2),
    boxShadow: '0 8px 32px rgba(0, 59, 112, 0.4)',
    backdropFilter: 'blur(10px)',
  },
  awardList: {
    maxHeight: 300,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

const getCardImg = (areaId, lot) => {
  if (areaId === 1) {
    return lot.documents?.[0]?.url || 'https://media.istockphoto.com/photos/tractor-picture-id173584806';
  }
  return lot.documents?.[0]?.url || 'https://s3-us-west-2.amazonaws.com/enterreno-production/moments/photos/000/006/806/thumb/0598-torre-santa-maria-santiago-1988.JPG';
};


const NabvarAwards = () => {
  const classes = useStyles();
  const user = useContext(UserContext);
  const { auction } = useContext(AuctionContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const { areaId, awards, lots } = auction;

  const lotList = useMemo(() => {
    if (!awards || !user?.userData?.externalId) return [];
    return awards
      .filter((award) => award.user.externalId === user.userData.externalId)
      .map((award) => ({
        ...lots.find((lot) => award.lot.id === lot.id),
        price: award.offer.amount,
        status: LOT_STATUS.PENDIENTE,
      }));
  }, [awards, lots, user]);

  const totalCLP = useMemo(() => lotList.reduce((ac, cr) => ac + (cr.unit === '$' ? cr.price : 0), 0), [lotList]);
  const totalUF = useMemo(() => lotList.reduce((ac, cr) => ac + (cr.unit === 'UF' ? cr.price : 0), 0), [lotList]);
  const totalUSD = useMemo(() => lotList.reduce((ac, cr) => ac + (cr.unit === 'USD' ? cr.price : 0), 0), [lotList]);
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const AwardList = () => {
    if (lotList.length === 0) return null;
    return (
      lotList.map((lot, idx) => (
        <Box key={idx} className={classes.awardItem}>
          <Box className={classes.awardImgBox}>
            <img
              alt="lot thumbnail"
              style={{ height: '100%', width: '100%', objectFit: 'cover' }}
              src={getCardImg(areaId, lot)}
            />
          </Box>
          <Box className={classes.awardInfoBox}>
            <NormalText fontSize={14} color={'#003A70'} fontWeight={600}>
              {`LOTE: ${lot.number}`}
            </NormalText>
            <NormalText fontSize={13} color={'#2d5aa0'}>
              {`${lot.name}`}
            </NormalText>
            <NormalText fontSize={14} color={'#004B8D'} fontWeight={500}>
              {`Adjudicado ${formatMoney(lot.price, lot.unit)}`}
            </NormalText>
          </Box>
        </Box>
      ))
    )
  }

  return (
    <div>
      <Box className={classes.navBarTextBox} onClick={handleOpen}>
        <Typography variant="subtitle2" className={classes.title}>
          Mis Adjudicaciones
        </Typography>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={0}
        PaperProps={{ square: true, style: { backgroundColor: 'transparent' } }}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
      >
        <Box className={classes.popoverBox}>
          {totalCLP ? (
            <Typography className={classes.total}>
              Total Comprado: {formatMoney(totalCLP, '$')}
            </Typography>
          ) : null}
          {totalUF ? (
            <Typography className={classes.total}>
              Total Comprado: {formatMoney(totalUF, 'UF')}
            </Typography>
          ) : null}
          {totalUSD ? (
            <Typography className={classes.total}>
              Total Comprado: {formatMoney(totalUSD, 'USD')}
            </Typography>
          ) : null}
          <Typography className={classes.titleSmall}>
            Cantidad Comprada: {lotList.length}
          </Typography>
          <Typography className={classes.paraph}>
            Una vez cerrado el remate, te contactaremos para cerrar las ventas de tus respectivas adjudicaciones
          </Typography>
          <Box className={classes.awardList}>
            <AwardList />
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

export default NabvarAwards;
